import {
  formatDataValue,
  formatTemperature,
} from '../../../../helpers/data-helpers';
import {
  formatUvIndexHeader,
  formatWindSpeedDisplay,
} from '../../helpers/graph/graphData';

export const WEATHER_FORECAST_DATA_NOT_AVAILABLE = 'Not available';

export interface WeatherSummaryApiResponse {
  description?: string;
  weatherIcon?: string;
  temperature?: {
    minimum?: number;
    maximum?: number;
    current?: number;
    feelsLike?: number;
    trend?: number;
  };
}

export interface WeatherCurrentConditionsApiResponse {
  humidity?: { current?: number; trend?: number };
  rain?: { amountToday?: number; chance?: number };
  wind?: {
    speed?: number;
    direction?: number;
    compass?: string;
    trend?: number;
  };
  airPressure?: { current?: number; trend?: number };
  uv?: { current?: number | null };
  dewPoint?: { current?: number | null };
}

export interface WeatherForecastCurrentConditionsMetric {
  label: string;
  value: string;
}

export interface WeatherForecastCurrentConditionsViewModel {
  weatherIcon?: string;
  temperatureDisplay: string | null;
  feelsLikeDisplay: string | null;
  highTempDisplay: string | null;
  lowTempDisplay: string | null;
  description?: string;
  metrics: WeatherForecastCurrentConditionsMetric[];
}

interface UnitsConfig {
  temperature?: string;
  windSpeed?: string;
  [key: string]: unknown;
}

const formatTemperatureWithDegree = (
  temperature: number | null | undefined,
  unitsConfig: UnitsConfig,
  rounded = true,
) => {
  const formatted = formatTemperature(temperature, unitsConfig, true, rounded);

  if (formatted == null) {
    return null;
  }

  return `${formatted}\u00B0`;
};

export const formatWeatherForecastCurrentConditions = (
  summary: WeatherSummaryApiResponse | null | undefined,
  currentConditions: WeatherCurrentConditionsApiResponse | null | undefined,
  unitsConfig: UnitsConfig,
): WeatherForecastCurrentConditionsViewModel => {
  const temperatureCurrent = summary?.temperature?.current;
  const temperatureFeelsLike = summary?.temperature?.feelsLike;
  const temperatureMaximum = summary?.temperature?.maximum;
  const temperatureMinimum = summary?.temperature?.minimum;

  const windSpeedMetresPerSecond = currentConditions?.wind?.speed;
  const windCompass = currentConditions?.wind?.compass?.trim();

  const windDisplayValue =
    windSpeedMetresPerSecond != null
      ? formatWindSpeedDisplay(
          windSpeedMetresPerSecond,
          unitsConfig,
          windCompass,
        ) || WEATHER_FORECAST_DATA_NOT_AVAILABLE
      : WEATHER_FORECAST_DATA_NOT_AVAILABLE;

  const humidityDisplayValue = formatDataValue(
    currentConditions?.humidity?.current,
    '',
    '%',
    WEATHER_FORECAST_DATA_NOT_AVAILABLE,
  );

  const pressureDisplayValue = formatDataValue(
    currentConditions?.airPressure?.current,
    '',
    ' hPa',
    WEATHER_FORECAST_DATA_NOT_AVAILABLE,
  );

  const uvCurrent = currentConditions?.uv?.current;
  const uvDisplayValue =
    uvCurrent != null && Number.isFinite(uvCurrent)
      ? formatUvIndexHeader(uvCurrent)
      : WEATHER_FORECAST_DATA_NOT_AVAILABLE;

  const dewPointDisplayValue =
    formatTemperatureWithDegree(
      currentConditions?.dewPoint?.current,
      unitsConfig,
    ) ?? WEATHER_FORECAST_DATA_NOT_AVAILABLE;

  const rainTodayDisplayValue = formatDataValue(
    currentConditions?.rain?.amountToday,
    '',
    'mm',
    '—',
  );

  return {
    weatherIcon: summary?.weatherIcon,
    temperatureDisplay: formatTemperatureWithDegree(
      temperatureCurrent,
      unitsConfig,
    ),
    feelsLikeDisplay: formatTemperatureWithDegree(
      temperatureFeelsLike,
      unitsConfig,
    ),
    highTempDisplay: formatTemperatureWithDegree(
      temperatureMaximum,
      unitsConfig,
    ),
    lowTempDisplay: formatTemperatureWithDegree(
      temperatureMinimum,
      unitsConfig,
    ),
    description: summary?.description,
    metrics: [
      { label: 'Wind', value: windDisplayValue },
      { label: 'Humidity', value: humidityDisplayValue },
      { label: 'Pressure', value: pressureDisplayValue },
      {
        label: 'UV rating',
        value: uvDisplayValue,
      },
      {
        label: 'Dew point',
        value: dewPointDisplayValue,
      },
      {
        label: 'Rain today',
        value: rainTodayDisplayValue,
      },
    ],
  };
};
